import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const MAX_RETRY_ATTEMPTS = 5

const initialState = () => ({
  eventSource: null,
  retryAttempts: 0,
  toyUpdates: {},
  isPageVisible: true,
  connectionLostWhileHidden: false,
  initialized: false
})

const state = initialState()

const mutations = {
  FORCE_CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      console.warn('Fechando SSE manualmente (force close).')
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_INITIALIZED(state, value) {
    state.initialized = value
  },
  SET_EVENT_SOURCE(state, eventSource) {
    state.eventSource = eventSource
  },
  CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_RETRY_ATTEMPTS(state, attempts) {
    state.retryAttempts = attempts
  },
  SET_PAGE_VISIBILITY(state, isVisible) {
    state.isPageVisible = isVisible
  },
  SET_CONNECTION_LOST_WHILE_HIDDEN(state, value) {
    state.connectionLostWhileHidden = value
  },
  // Mutation para atualizar os brinquedos de um shopping
  // O payload é esperado no formato: { [shoppingId]: [array de brinquedos] }
  SET_TOY_UPDATE(state, payload) {
    Object.keys(payload).forEach((shoppingId) => {
      // Sobrescreve (ou adiciona) o update para o shoppingId especificado
      Vue.set(state?.toyUpdates, shoppingId, payload[shoppingId])
    })
  },
  // 2) Mutation para resetar tudo
  RESET_STATE(state) {
    Object.assign(state, initialState())
  }
}

const actions = {
  // Inicializa os listeners de visibilidade da página
  initVisibilityListeners({ commit, dispatch }) {
    // Adiciona listener para o evento de visibilidade da página
    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState === 'visible'
      commit('SET_PAGE_VISIBILITY', isVisible)
      // Se a página ficou visível novamente e a conexão foi perdida enquanto estava oculta
      if (isVisible) {
        dispatch('checkAndReconnectIfNeeded')
      }
    })
    // Adiciona listener para quando a janela recebe foco
    window.addEventListener('focus', () => {
      commit('SET_PAGE_VISIBILITY', true)
      dispatch('checkAndReconnectIfNeeded')
    })
    // Adiciona listener para quando a janela perde foco
    window.addEventListener('blur', () => {
      commit('SET_PAGE_VISIBILITY', false)
    })
  },
  // Verifica o estado da conexão e reconecta se necessário
  checkAndReconnectIfNeeded({ commit, dispatch, state }) {
    // Se a conexão foi perdida enquanto a página estava oculta, reconecta
    console.log('checando e reconectando se necessário')
    if (state.connectionLostWhileHidden) {
      console.log('Reconectando após página ficar visível novamente...')
      commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    }
    // Se não há conexão ativa ou se o readyState não é OPEN, tenta reconectar
    else if (
      !state.eventSource ||
      (state.eventSource && state.eventSource.readyState !== EventSource.OPEN)
    ) {
      console.log('Conexão SSE não está ativa, reconectando...')
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    }
  },
  startSSE({ commit, dispatch, rootGetters, state }) {
    // Obter o id do usuário e as lojas permitidas a partir dos getters do módulo "user"
    const currentUserId = rootGetters['user/currentUserId']
    const userStores = rootGetters['user/currentUserStores']

    if (!currentUserId || !userStores || userStores.length === 0) {
      console.error('ID do usuário ou lojas não definidas.')
      return
    }

    // Extrair os IDs das lojas
    const storeIds = userStores.map((store) => store.id)
    console.log(
      'Conectando SSE para usuário:',
      currentUserId,
      'com as lojas:',
      storeIds
    )

    commit('CLOSE_EVENT_SOURCE')

    // Montar a URL com os parâmetros: userId e stores (array convertido para JSON)
    const apiUrl = process.env.VUE_APP_API_URL
    const url = `${apiUrl}/stores/stream/sse?userId=${encodeURIComponent(
      currentUserId
    )}&stores=${encodeURIComponent(JSON.stringify(storeIds))}`

    const eventSource = new EventSource(url)

    // Tratamento de recebimento de mensagens do SSE
    eventSource.onmessage = (event) => {
      try {
        // Exemplo de payload recebido:
        // { "4": [ array de brinquedos ], "6": [ array de brinquedos ] }
        const eventData = JSON.parse(event.data)
        console.log('Evento SSE recebido:', eventData)
        // Salva ou sobrescreve a atualização para cada shopping recebido
        commit('SET_TOY_UPDATE', eventData)
        commit('SET_RETRY_ATTEMPTS', 0)
      } catch (err) {
        console.error('Erro ao parsear dados SSE:', err)
      }
    }

    // Detecta reconexão (após uma falha)
    eventSource.onopen = () => {
      if (state.retryAttempts > 0) {
        console.log('Reconectado ao SSE após falha anterior!')

        // Emite o evento global para o componente banner
        window.dispatchEvent(
          new CustomEvent('sse:reconnected', {
            detail: {
              at: new Date(),
              attempts: state.retryAttempts
            }
          })
        )

        // Zera contador de tentativas
        commit('SET_RETRY_ATTEMPTS', 0)
      }
    }

    // Tratamento de erro na conexão SSE
    eventSource.onerror = (error) => {
      console.error('Erro na conexão SSE:', error)
      commit('CLOSE_EVENT_SOURCE')

      // Incrementa o contador de tentativas
      const currentAttempts = state.retryAttempts + 1
      commit('SET_RETRY_ATTEMPTS', currentAttempts)

      // Sempre tenta reconectar automaticamente até 5 vezes
      if (currentAttempts <= MAX_RETRY_ATTEMPTS) {
        console.warn(
          `Tentando reconectar automaticamente... (Tentativa ${currentAttempts} de ${MAX_RETRY_ATTEMPTS})`
        )
        // Adiciona um atraso de 2 segundos antes de tentar reconectar
        setTimeout(() => {
          dispatch('startSSE')
        }, 2000)
      } else {
        console.error(
          `Falha na reconexão após ${MAX_RETRY_ATTEMPTS} tentativas. Recarregando a página forçadamente.`
        )
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    }
    commit('SET_EVENT_SOURCE', eventSource)
  },

  stopSSE({ commit }) {
    commit('CLOSE_EVENT_SOURCE')
    commit('SET_RETRY_ATTEMPTS', 0)
    commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
  },
  // Método para inicializar o módulo
  init({ dispatch, commit, state }) {
    if (state.initialized) return
    commit('SET_INITIALIZED', true)
    // Inicializa os listeners de visibilidade
    dispatch('initVisibilityListeners')
    // Inicia a conexão SSE
    dispatch('startSSE')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
